import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  createSupportTicket,
  fetchMySupportTickets,
  fetchAllSupportTickets,
  updateSupportTicketStatus,
  SUPPORT_TICKETS_PAGE_SIZE,
} from '../../services/support'
import { SUPPORT_STATUS } from '../../config/settings/support'
import { getLogger } from '../../core/debuggers'

const logger = getLogger('Support.slice.js')

const initialState = {
  myTickets: [],
  adminTickets: [],
  adminPage: 0,
  adminPageSize: SUPPORT_TICKETS_PAGE_SIZE,
  adminTotal: 0,
  adminStatusFilter: SUPPORT_STATUS.OPEN,
  updatingTicketId: null,
  status: {
    create: 'idle',
    fetchMine: 'idle',
    fetchAdmin: 'idle',
    update: 'idle',
  },
  error: null,
  createSuccess: false,
  lastCreatedTrackingCode: null,
}

export const onCreateSupportTicketAsync = createAsyncThunk(
  'support/create',
  async ({ category, message }, { getState, rejectWithValue }) => {
    try {
      const userId = getState().adminAuth.user?.id
      if (!userId) return rejectWithValue('User not authenticated')
      return await createSupportTicket({ userId, category, message })
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message || 'No se pudo enviar el mensaje')
    }
  }
)

export const onFetchMySupportTicketsAsync = createAsyncThunk(
  'support/fetchMine',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = getState().adminAuth.user?.id
      if (!userId) return rejectWithValue('User not authenticated')
      return await fetchMySupportTickets(userId)
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message)
    }
  }
)

export const onFetchAdminSupportTicketsAsync = createAsyncThunk(
  'support/fetchAdmin',
  async ({ status, page = 0 } = {}, { getState, rejectWithValue }) => {
    try {
      const filter =
        status === undefined
          ? getState().support.adminStatusFilter
          : status
      return await fetchAllSupportTickets({
        status: filter || null,
        page,
      })
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message)
    }
  }
)

export const onUpdateSupportTicketStatusAsync = createAsyncThunk(
  'support/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      return await updateSupportTicketStatus(id, status)
    } catch (error) {
      logger.error(error)
      return rejectWithValue({ id, message: error.message })
    }
  }
)

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    resetSupportCreateState: (state) => {
      state.status.create = 'idle'
      state.createSuccess = false
      state.lastCreatedTrackingCode = null
      state.error = null
    },
    setAdminStatusFilter: (state, action) => {
      state.adminStatusFilter = action.payload
    },
    removeAdminTicket: (state, action) => {
      const id = action.payload
      state.adminTickets = state.adminTickets.filter((ticket) => ticket.id !== id)
    },
    resetSupportState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(onCreateSupportTicketAsync.pending, (state) => {
        state.status.create = 'loading'
        state.createSuccess = false
        state.lastCreatedTrackingCode = null
        state.error = null
      })
      .addCase(onCreateSupportTicketAsync.fulfilled, (state, action) => {
        state.status.create = 'succeeded'
        state.createSuccess = true
        state.lastCreatedTrackingCode = action.payload?.trackingCode || null
        if (action.payload) {
          state.myTickets = [action.payload, ...state.myTickets]
        }
      })
      .addCase(onCreateSupportTicketAsync.rejected, (state, action) => {
        state.status.create = 'failed'
        state.error = action.payload
      })

      .addCase(onFetchMySupportTicketsAsync.pending, (state) => {
        state.status.fetchMine = 'loading'
        state.error = null
      })
      .addCase(onFetchMySupportTicketsAsync.fulfilled, (state, action) => {
        state.status.fetchMine = 'succeeded'
        state.myTickets = action.payload || []
      })
      .addCase(onFetchMySupportTicketsAsync.rejected, (state, action) => {
        state.status.fetchMine = 'failed'
        state.error = action.payload
      })

      .addCase(onFetchAdminSupportTicketsAsync.pending, (state) => {
        state.status.fetchAdmin = 'loading'
        state.error = null
      })
      .addCase(onFetchAdminSupportTicketsAsync.fulfilled, (state, action) => {
        const { data, total, page, pageSize } = action.payload || {}
        state.status.fetchAdmin = 'succeeded'
        state.adminTickets = data || []
        state.adminTotal = total ?? 0
        state.adminPage = page ?? 0
        state.adminPageSize = pageSize ?? SUPPORT_TICKETS_PAGE_SIZE
      })
      .addCase(onFetchAdminSupportTicketsAsync.rejected, (state, action) => {
        state.status.fetchAdmin = 'failed'
        state.error = action.payload
      })

      .addCase(onUpdateSupportTicketStatusAsync.pending, (state, action) => {
        state.status.update = 'loading'
        state.updatingTicketId = action.meta.arg.id
      })
      .addCase(onUpdateSupportTicketStatusAsync.fulfilled, (state, action) => {
        state.status.update = 'succeeded'
        state.updatingTicketId = null
        const updated = action.payload
        const idx = state.adminTickets.findIndex((t) => t.id === updated?.id)
        if (idx !== -1 && updated) {
          state.adminTickets[idx] = { ...state.adminTickets[idx], ...updated }
        }
      })
      .addCase(onUpdateSupportTicketStatusAsync.rejected, (state, action) => {
        state.status.update = 'failed'
        state.updatingTicketId = null
        state.error = action.payload?.message || action.payload
      })
  },
})

export const {
  resetSupportCreateState,
  setAdminStatusFilter,
  removeAdminTicket,
  resetSupportState,
} = supportSlice.actions

export default supportSlice.reducer
